import { formatInTimeZone } from 'date-fns-tz';
import { useMemo, useState } from 'react';
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { AgentRunDurationTrendPanel } from '@apps/web/components/admin/outreach_observability/agent_run_duration_trend_panel';
import {
    type HourOfDayPoint,
    toHourOfDaySeries,
} from '@apps/web/components/admin/outreach_observability/comparison_utils';
import {
    echoedOutreachWindow,
    OUTREACH_ANALYTICS_TIMEZONE,
    OUTREACH_REFRESH_INTERVAL_MS,
    type OutreachTabProps,
    outreachWindowSummary,
} from '@apps/web/components/admin/outreach_observability/constants';
import { GhostLineChart } from '@apps/web/components/admin/outreach_observability/ghost_line_chart';
import { LoopRunsPanel } from '@apps/web/components/admin/outreach_observability/loop_runs_panel';
import {
    type KpiRunMetric,
    RunsDrillDialog,
    type RunsDrillFilters,
} from '@apps/web/components/admin/outreach_observability/runs_drill_dialog';
import { ShiftDurationHistogram } from '@apps/web/components/admin/outreach_observability/shift_duration_histogram';
import { TokensDurationScatter } from '@apps/web/components/admin/outreach_observability/tokens_duration_scatter';
import { ToolDetailDrawer } from '@apps/web/components/admin/outreach_observability/tool_detail_drawer';
import {
    formatCompact,
    ToolStatsTable,
} from '@apps/web/components/admin/outreach_observability/tool_stats_table';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import {
    getDateStringInTimezone,
    shiftDateKey,
} from '@apps/web/lib/date_utils';
import {
    useAdminAgentRunDurationTrends,
    useAdminOutreachObservability,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type AgentRunDurationTrendBucketRead,
    type HourlyKpiBucket,
    type ToolStatRow,
} from '@libraries/typescript/api_client';

// Eight ET days of hourly buckets: enough to slice today, yesterday, and the
// same weekday last week for the ghost comparison overlays.
const DURATION_WINDOW_HOURS = 192;

// Maps a ghost-chart series key to how many ET calendar days back it plots, so a
// clicked point drills into that series' actual ET date. Mirrors the ghost
// ordering built in buildDurationSeries (index 0 = yesterday, index 1 = 7d).
const DAY_OFFSET_BY_SERIES: Record<string, number> = {
    today: 0,
    ghost_0: 1,
    ghost_1: 7,
};
const LABEL_BY_SERIES: Record<string, string> = {
    today: 'Today',
    ghost_0: 'Yesterday',
    ghost_1: '7 days ago',
};

function etDateKey(iso: string): string {
    return formatInTimeZone(
        new Date(iso),
        OUTREACH_ANALYTICS_TIMEZONE,
        'yyyy-MM-dd',
    );
}

// Calendar-day arithmetic on the ET date key: take today's ET date, then step
// back `daysAgo` whole days. Doing the subtraction on the date key (not on an
// absolute timestamp) keeps the result on the correct ET calendar day across
// DST transitions, where a day is not always 24h of absolute time.
function etDateKeyDaysAgo(daysAgo: number): string {
    const todayKey = getDateStringInTimezone(
        new Date(),
        OUTREACH_ANALYTICS_TIMEZONE,
    );
    return shiftDateKey(todayKey, -daysAgo);
}

function formatEtHour(hour: number): string {
    const period = hour < 12 ? 'AM' : 'PM';
    const display = hour % 12 === 0 ? 12 : hour % 12;
    return `${display} ${period}`;
}

function formatDurationMs(value: number): string {
    const seconds = value / 1000;
    if (seconds < 60) {
        return `${seconds.toFixed(1)}s`;
    }
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${Math.round(seconds % 60)}s`;
}

// Slices the 8-day hourly series into today / yesterday / same-weekday-last-week
// points on the shared ET hour-of-day axis for one duration metric.
function buildDurationSeries(
    buckets: Array<AgentRunDurationTrendBucketRead>,
    accessor: (bucket: AgentRunDurationTrendBucketRead) => number | null,
): {
    readonly today: Array<HourOfDayPoint>;
    readonly ghosts: Array<{
        readonly label: string;
        readonly points: Array<HourOfDayPoint>;
    }>;
} {
    const forDay = (key: string): Array<HourOfDayPoint> =>
        toHourOfDaySeries(
            buckets.filter((bucket) => etDateKey(bucket.bucket_start) === key),
            accessor,
        );
    return {
        today: forDay(etDateKeyDaysAgo(0)),
        ghosts: [
            { label: 'Yesterday', points: forDay(etDateKeyDaysAgo(1)) },
            { label: '7 days ago', points: forDay(etDateKeyDaysAgo(7)) },
        ],
    };
}

type RunsDrill = {
    readonly title: string;
    readonly description?: string;
    readonly metric: KpiRunMetric;
    readonly filters?: RunsDrillFilters;
};

type TokenTrendPoint = {
    readonly label: string;
    readonly value: number | null;
    // ET hour-of-day and date key of the bucket, so a clicked point drills into
    // that exact ET hour on that ET day.
    readonly hourOfDayEt: number;
    readonly dayEt: string;
};

type ContextUtilizationPoint = {
    readonly label: string;
    readonly average: number | null;
    readonly maximum: number | null;
    readonly contributingRuns: number;
    readonly hourOfDayEt: number;
    readonly dayEt: string;
};

function TokenTrendTooltip({
    active,
    payload,
    label,
}: {
    readonly active?: boolean;
    readonly payload?: Array<{ readonly value?: number | null }>;
    readonly label?: string;
}) {
    if (!active || !payload?.[0]) {
        return null;
    }
    const value = payload[0].value;
    return (
        <div className="bg-background rounded-md border px-3 py-2 text-xs shadow-sm">
            <div className="mb-1 font-medium">{label}</div>
            <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Output tokens/run</span>
                <span className="tabular-nums">
                    {value === null || value === undefined
                        ? '—'
                        : formatCompact(Math.round(value))}
                </span>
            </div>
        </div>
    );
}

function OutputTokensChart({
    buckets,
    onPointClick,
}: {
    readonly buckets: Array<HourlyKpiBucket>;
    readonly onPointClick?: (point: TokenTrendPoint) => void;
}) {
    const data: Array<TokenTrendPoint> = buckets.map((bucket) => ({
        label: formatInTimeZone(
            new Date(bucket.bucket_start),
            OUTREACH_ANALYTICS_TIMEZONE,
            'M/d ha',
        ),
        value:
            bucket.num_runs === 0
                ? null
                : bucket.total_output_tokens / bucket.num_runs,
        hourOfDayEt: Number(
            formatInTimeZone(
                new Date(bucket.bucket_start),
                OUTREACH_ANALYTICS_TIMEZONE,
                'H',
            ),
        ),
        dayEt: formatInTimeZone(
            new Date(bucket.bucket_start),
            OUTREACH_ANALYTICS_TIMEZONE,
            'yyyy-MM-dd',
        ),
    }));
    if (data.length === 0) {
        return (
            <div className="text-muted-foreground flex h-64 items-center justify-center rounded-md border text-sm">
                No runs to plot.
            </div>
        );
    }
    return (
        <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ left: 8, right: 8 }}>
                    <CartesianGrid stroke="var(--border)" vertical={false} />
                    <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11 }}
                        minTickGap={28}
                    />
                    <YAxis
                        tick={{ fontSize: 11 }}
                        width={44}
                        tickFormatter={(value: number) => formatCompact(value)}
                    />
                    <RechartsTooltip content={<TokenTrendTooltip />} />
                    <Line
                        type="monotone"
                        dataKey="value"
                        stroke="var(--chart-1)"
                        dot={false}
                        activeDot={
                            onPointClick
                                ? (props: {
                                      readonly cx?: number;
                                      readonly cy?: number;
                                      readonly payload?: TokenTrendPoint;
                                  }) => {
                                      if (
                                          props.cx === undefined ||
                                          props.cy === undefined ||
                                          props.payload === undefined
                                      ) {
                                          return <circle r={0} />;
                                      }
                                      const point = props.payload;
                                      return (
                                          <circle
                                              cx={props.cx}
                                              cy={props.cy}
                                              r={5}
                                              fill="var(--chart-1)"
                                              style={{ cursor: 'pointer' }}
                                              onClick={() =>
                                                  onPointClick(point)
                                              }
                                          />
                                      );
                                  }
                                : undefined
                        }
                        strokeWidth={2}
                        connectNulls={false}
                        isAnimationActive={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

function ContextUtilizationTooltip({
    active,
    payload,
    label,
}: {
    readonly active?: boolean;
    readonly payload?: Array<{
        readonly payload?: ContextUtilizationPoint;
    }>;
    readonly label?: string;
}) {
    const point = payload?.[0]?.payload;
    if (!active || point === undefined) {
        return null;
    }
    return (
        <div className="bg-background rounded-md border px-3 py-2 text-xs shadow-sm">
            <div className="mb-1 font-medium">{label}</div>
            <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Avg peak ctx</span>
                <span className="tabular-nums">
                    {point.average === null
                        ? '—'
                        : `${(point.average * 100).toFixed(0)}%`}
                </span>
            </div>
            <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Max peak ctx</span>
                <span className="tabular-nums">
                    {point.maximum === null
                        ? '—'
                        : `${(point.maximum * 100).toFixed(0)}%`}
                </span>
            </div>
            <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Contributing runs</span>
                <span className="tabular-nums">
                    {formatCompact(point.contributingRuns)}
                </span>
            </div>
        </div>
    );
}

function ContextUtilizationChart({
    buckets,
    onPointClick,
}: {
    readonly buckets: Array<HourlyKpiBucket>;
    readonly onPointClick?: (point: ContextUtilizationPoint) => void;
}) {
    const data: Array<ContextUtilizationPoint> = buckets.map((bucket) => ({
        label: formatInTimeZone(
            new Date(bucket.bucket_start),
            OUTREACH_ANALYTICS_TIMEZONE,
            'M/d ha',
        ),
        average:
            bucket.context_util_run_count === 0
                ? null
                : bucket.context_util_sum / bucket.context_util_run_count,
        maximum:
            bucket.context_util_run_count === 0
                ? null
                : bucket.context_util_max,
        contributingRuns: bucket.context_util_run_count,
        hourOfDayEt: Number(
            formatInTimeZone(
                new Date(bucket.bucket_start),
                OUTREACH_ANALYTICS_TIMEZONE,
                'H',
            ),
        ),
        dayEt: formatInTimeZone(
            new Date(bucket.bucket_start),
            OUTREACH_ANALYTICS_TIMEZONE,
            'yyyy-MM-dd',
        ),
    }));
    if (data.length === 0) {
        return (
            <div className="text-muted-foreground flex h-64 items-center justify-center rounded-md border text-sm">
                No runs to plot.
            </div>
        );
    }
    return (
        <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ left: 8, right: 8 }}>
                    <CartesianGrid stroke="var(--border)" vertical={false} />
                    <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11 }}
                        minTickGap={28}
                    />
                    <YAxis
                        domain={[0, 1]}
                        ticks={[0, 0.25, 0.5, 0.75, 1]}
                        tick={{ fontSize: 11 }}
                        width={44}
                        tickFormatter={(value: number) =>
                            `${(value * 100).toFixed(0)}%`
                        }
                        allowDataOverflow={true}
                    />
                    <RechartsTooltip content={<ContextUtilizationTooltip />} />
                    <Line
                        type="monotone"
                        dataKey="average"
                        name="Average"
                        stroke="var(--chart-1)"
                        dot={false}
                        activeDot={
                            onPointClick
                                ? (props: {
                                      readonly cx?: number;
                                      readonly cy?: number;
                                      readonly payload?: ContextUtilizationPoint;
                                  }) => {
                                      if (
                                          props.cx === undefined ||
                                          props.cy === undefined ||
                                          props.payload === undefined
                                      ) {
                                          return <circle r={0} />;
                                      }
                                      const point = props.payload;
                                      return (
                                          <circle
                                              cx={props.cx}
                                              cy={props.cy}
                                              r={5}
                                              fill="var(--chart-1)"
                                              style={{ cursor: 'pointer' }}
                                              onClick={() =>
                                                  onPointClick(point)
                                              }
                                          />
                                      );
                                  }
                                : undefined
                        }
                        strokeWidth={2}
                        connectNulls={false}
                        isAnimationActive={false}
                    />
                    <Line
                        type="monotone"
                        dataKey="maximum"
                        name="Maximum"
                        stroke="var(--chart-2)"
                        strokeDasharray="5 5"
                        dot={false}
                        activeDot={
                            onPointClick
                                ? (props: {
                                      readonly cx?: number;
                                      readonly cy?: number;
                                      readonly payload?: ContextUtilizationPoint;
                                  }) => {
                                      if (
                                          props.cx === undefined ||
                                          props.cy === undefined ||
                                          props.payload === undefined
                                      ) {
                                          return <circle r={0} />;
                                      }
                                      const point = props.payload;
                                      return (
                                          <circle
                                              cx={props.cx}
                                              cy={props.cy}
                                              r={5}
                                              fill="var(--chart-2)"
                                              style={{ cursor: 'pointer' }}
                                              onClick={() =>
                                                  onPointClick(point)
                                              }
                                          />
                                      );
                                  }
                                : undefined
                        }
                        strokeWidth={2}
                        connectNulls={false}
                        isAnimationActive={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

function CompanionChip({
    label,
    value,
}: {
    readonly label: string;
    readonly value: string;
}) {
    return (
        <div className="flex flex-col rounded-md border px-3 py-1.5">
            <span className="text-muted-foreground text-xs tracking-wide uppercase">
                {label}
            </span>
            <span className="text-sm font-semibold tabular-nums">{value}</span>
        </div>
    );
}

export function AgentTab({
    organizationIds,
    window: liveWindow,
    urgency,
}: OutreachTabProps) {
    const { data: responseData } = useAdminOutreachObservability(
        {
            // No 'comparisons': this tab's ghost overlays come from the
            // duration-trends endpoint (durationQuery), not data.comparisons.
            sections: ['live', 'kpi_trends', 'tool_stats'],
            organizationIds,
            window: liveWindow,
            urgency,
        },
        { refreshIntervalMs: OUTREACH_REFRESH_INTERVAL_MS },
    );
    const data = responseData?.urgency === urgency ? responseData : undefined;
    const durationQuery = useAdminAgentRunDurationTrends({
        granularity: 'hour',
        window_hours: DURATION_WINDOW_HOURS,
        run_type: 'outreach_analysis',
        mode: 'live',
        urgency,
        ...(organizationIds.length > 0
            ? { organization_id: organizationIds }
            : {}),
    });
    const [selectedTool, setSelectedTool] = useState<ToolStatRow | null>(null);
    const [drill, setDrill] = useState<RunsDrill | null>(null);
    const activeWindow =
        (data ? echoedOutreachWindow(data) : null) ?? liveWindow;
    const windowSummary = outreachWindowSummary(activeWindow);

    function openHourDrill(
        hourOfDayEt: number,
        day: string,
        seriesLabel: string,
    ) {
        setDrill({
            title: `Runs at ${formatEtHour(hourOfDayEt)} ET`,
            description: `${seriesLabel} · ${day}`,
            metric: 'runs',
            filters: { hourOfDayEt, day },
        });
    }

    function openGhostDrill(hourOfDay: number, seriesKey: string) {
        const offset = DAY_OFFSET_BY_SERIES[seriesKey] ?? 0;
        openHourDrill(
            hourOfDay,
            etDateKeyDaysAgo(offset),
            LABEL_BY_SERIES[seriesKey] ?? 'Today',
        );
    }

    const durationBuckets = useMemo(
        () => durationQuery.data?.buckets ?? [],
        [durationQuery.data?.buckets],
    );
    const runDurationSeries = useMemo(
        () =>
            buildDurationSeries(
                durationBuckets,
                (bucket) => bucket.p95_duration_ms,
            ),
        [durationBuckets],
    );
    const firstGenSeries = useMemo(
        () =>
            buildDurationSeries(
                durationBuckets,
                (bucket) => bucket.p95_first_gen_latency_ms,
            ),
        [durationBuckets],
    );

    const kpiBuckets = data?.kpi_hourly_trends ?? [];
    const tokenTotals = kpiBuckets.reduce(
        (acc, bucket) => ({
            runs: acc.runs + bucket.num_runs,
            llmCalls: acc.llmCalls + bucket.llm_call_count,
            compactionRuns: acc.compactionRuns + bucket.compaction_run_count,
            ctxUtilSum: acc.ctxUtilSum + bucket.context_util_sum,
            ctxUtilRuns: acc.ctxUtilRuns + bucket.context_util_run_count,
        }),
        {
            runs: 0,
            llmCalls: 0,
            compactionRuns: 0,
            ctxUtilSum: 0,
            ctxUtilRuns: 0,
        },
    );
    const llmCallsPerRun =
        tokenTotals.runs === 0
            ? '—'
            : (tokenTotals.llmCalls / tokenTotals.runs).toFixed(1);
    const compactionRate =
        tokenTotals.runs === 0
            ? '—'
            : `${((tokenTotals.compactionRuns / tokenTotals.runs) * 100).toFixed(1)}%`;
    const averagePeakContextUtilization =
        tokenTotals.ctxUtilRuns === 0
            ? '—'
            : `${((tokenTotals.ctxUtilSum / tokenTotals.ctxUtilRuns) * 100).toFixed(0)}%`;

    const toolStats = data?.tool_stats;

    return (
        <div className="flex flex-col gap-4">
            <div className="grid gap-4 lg:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle>Run duration p95</CardTitle>
                        <CardDescription>
                            Outreach analysis run duration p95 by ET hour of day
                            — today against yesterday and the same weekday last
                            week.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <GhostLineChart
                            today={runDurationSeries.today}
                            ghosts={runDurationSeries.ghosts}
                            valueFormatter={formatDurationMs}
                            onPointClick={({ hourOfDay, seriesKey }) =>
                                openGhostDrill(hourOfDay, seriesKey)
                            }
                        />
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle>First gen latency p95</CardTitle>
                        <CardDescription>
                            Time to first generated response p95 by ET hour of
                            day — a stored proxy for true TTFT, compared across
                            days.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <GhostLineChart
                            today={firstGenSeries.today}
                            ghosts={firstGenSeries.ghosts}
                            valueFormatter={formatDurationMs}
                            onPointClick={({ hourOfDay, seriesKey }) =>
                                openGhostDrill(hourOfDay, seriesKey)
                            }
                        />
                    </CardContent>
                </Card>
            </div>
            <Card>
                <CardHeader>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex flex-col gap-1.5">
                            <CardTitle>Output tokens per run</CardTitle>
                            <CardDescription>
                                Hourly, last 48 hours (America/New_York). No
                                ghost overlay — the comparison buckets do not
                                carry token totals.
                            </CardDescription>
                        </div>
                        <div className="flex gap-2">
                            <CompanionChip
                                label="LLM calls / run"
                                value={llmCallsPerRun}
                            />
                            <CompanionChip
                                label="Compaction rate"
                                value={compactionRate}
                            />
                            <CompanionChip
                                label="Avg peak ctx"
                                value={averagePeakContextUtilization}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <OutputTokensChart
                        buckets={kpiBuckets}
                        onPointClick={(point) =>
                            openHourDrill(
                                point.hourOfDayEt,
                                point.dayEt,
                                'Output tokens',
                            )
                        }
                    />
                </CardContent>
            </Card>
            <Card>
                <CardHeader>
                    <CardTitle>Peak context utilization per run</CardTitle>
                    <CardDescription>
                        Hourly average and maximum peak context-window
                        utilization per run, last 48 hours (America/New_York).
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <ContextUtilizationChart
                        buckets={kpiBuckets}
                        onPointClick={(point) =>
                            openHourDrill(
                                point.hourOfDayEt,
                                point.dayEt,
                                'Peak context utilization',
                            )
                        }
                    />
                </CardContent>
            </Card>
            <LoopRunsPanel loopRuns={toolStats?.loop_runs ?? []} />
            <Card>
                <CardHeader>
                    <CardTitle>Tool calls</CardTitle>
                    <CardDescription>
                        Per-tool latency, error rate, retries, argument size,
                        and attributed output tokens (trailing 48h). Click a row
                        for the hourly trend and slowest calls.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <ToolStatsTable
                        tools={toolStats?.tools ?? []}
                        onSelectTool={(toolName) =>
                            setSelectedTool(
                                toolStats?.tools.find(
                                    (tool) => tool.tool_name === toolName,
                                ) ?? null,
                            )
                        }
                    />
                </CardContent>
            </Card>
            <TokensDurationScatter scatter={data?.scatter ?? []} />
            <AgentRunDurationTrendPanel
                defaultRunType="outreach_analysis"
                organizationIds={organizationIds}
                mode="live"
                urgency={urgency}
            />
            <Card>
                <CardHeader>
                    <CardTitle>Run time by shift count</CardTitle>
                    <CardDescription>
                        Average outreach run duration ({windowSummary}) bucketed
                        by how many shifts the outreach covered. Hover for min,
                        average, max, and run count.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <ShiftDurationHistogram
                        buckets={data?.shift_duration_histogram ?? []}
                        onBucketClick={(point) =>
                            setDrill({
                                title: `Runs covering ${point.label} shifts`,
                                description: `Runs bucketed by covered shift count (${windowSummary}).`,
                                metric: 'runs',
                                filters: {
                                    coveredShiftCountMin: point.minShiftCount,
                                    window: activeWindow,
                                    ...(point.maxShiftCount === null
                                        ? {}
                                        : {
                                              coveredShiftCountMax:
                                                  point.maxShiftCount,
                                          }),
                                },
                            })
                        }
                    />
                </CardContent>
            </Card>
            <ToolDetailDrawer
                tool={selectedTool}
                organizationIds={organizationIds}
                urgency={urgency}
                onClose={() => setSelectedTool(null)}
            />
            <RunsDrillDialog
                open={drill !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setDrill(null);
                    }
                }}
                title={drill?.title ?? ''}
                description={drill?.description}
                metric={drill?.metric ?? 'runs'}
                organizationIds={organizationIds}
                urgency={urgency}
                filters={drill?.filters}
            />
        </div>
    );
}
